import React, { ChangeEvent } from 'react';
import { Input, Form, Typography, Col, Alert, Space } from 'antd';
import { YoutubeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { FieldConfig } from '../../types/fieldTypes';
import { useValidator } from '../../hooks/useValidator';

const { Text } = Typography;

interface YouTubeVideoFieldProps {
  field: FieldConfig;
  value: any;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  mode: 'edit' | 'view';
  formData?: Record<string, any>;
}

export const YouTubeVideoField: React.FC<YouTubeVideoFieldProps> = ({
  field,
  value,
  onChange,
  mode,
  formData = {},
}) => {
  const { t } = useTranslation();
  const { touched, validationError, handleBlur, handleValidate } = useValidator(
    { field, value, formData }
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e);
    handleValidate(e.target.value);
  };

  if (mode === 'view') {
    return (
      <Col span={field.layout?.span || 24}>
        <Form.Item label={<Text type="secondary">{field.label}</Text>}>
          <Input value={value || 'No video URL provided'} disabled />
        </Form.Item>
      </Col>
    );
  }

  const colProps = {
    span: field.layout?.span || 24,
    offset: field.layout?.offset || 0,
    order: field.layout?.order,
  };

  return (
    <Col {...colProps} className={field.layout?.className}>
      <Form.Item
        label={field.label}
        required={field.required}
        validateStatus={validationError && touched ? 'error' : ''}
        help={
          <Space direction="vertical" size="small">
            {field.customProps?.helpText && (
              <Alert
                message={field.customProps.helpText}
                type="info"
                showIcon
              />
            )}
            {validationError && touched && (
              <Text type="danger">{validationError}</Text>
            )}
          </Space>
        }
      >
        <Input
          type="url"
          name={field.name}
          value={value || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={
            field.customProps?.placeholder ||
            'https://www.youtube.com/watch?v=...'
          }
          status={validationError && touched ? 'error' : ''}
          disabled={field.customProps?.disabled}
          size="large"
          addonBefore={
            <Space>
              <YoutubeOutlined style={{ color: '#ff0000' }} />
              {field.customProps?.buttonText || 'YouTube'}
            </Space>
          }
        />
      </Form.Item>

      {value && (
        <Space direction="vertical" style={{ marginTop: 16, width: '100%' }}>
          <Text strong>Preview:</Text>
          <div
            style={{
              position: 'relative',
              paddingBottom: '56.25%',
              height: 0,
              overflow: 'hidden',
            }}
          >
            <iframe
              src={`https://www.youtube.com/embed/${extractVideoId(value)}`}
              title={t('dynamic_form.youtube_video')}
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
            />
          </div>
        </Space>
      )}
    </Col>
  );
};

// Helper function to extract video ID from YouTube URL
function extractVideoId(url: string): string {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : '';
}
